import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { constantsData, selectionsData, urlsData } from "@/data";
import { getReleaseQuery } from "@/graph";
import { openAlert, setCurrentRelease, setReleaseTracks, setSelection } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useGetRelease = (id) => {
    const { isAuthenticated } = useAuth0();

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const { stereoReleaseTracks: stereoReleaseTracksSelectionKey } = selectionsData.keys;

    const { data, error, loading } = useQuery(getReleaseQuery, {
        skip: !isAuthenticated,
        variables: {
            where: { id },
        },
    });

    useEffect(() => {
        if (!data?.release) return;
        if (!loading && !data.release) navigate(urlsData.routes.notFound);

        dispatch(setCurrentRelease(data.release));

        const tracks = data.release?.releaseTracks?.map(({ id: releaseTrackId, track }) => ({
            ...track,
            releaseTrackId,
        }));

        const stereoTracks = tracks?.filter((track) => !track?.originalTrackId);

        dispatch(setReleaseTracks(tracks));
        dispatch(
            setSelection({
                [stereoReleaseTracksSelectionKey]: stereoTracks.map(({ id: trackId, title }) => ({
                    label: title,
                    value: trackId,
                })),
            }),
        );
    }, [data, loading]); // eslint-disable-line

    useEffect(
        () => {
            if (error?.message === constantsData.failedToFetch && !loading) {
                displayErrorHandler(t("messages.networkFailure"), dispatch, openAlert);
            }
        },
        [error, data], // eslint-disable-line
    );

    return {
        data: data?.release,
        loading,
    };
};

export default useGetRelease;
